// src/components/ShiftPrompt.jsx — "your shift has started; are you at the office?"
//
// Owner, need-care list 2026-09-02: "at the start of their shift they must be notified are you
// at the office if yes select it."
//
// ⚠️ WHY A BANNER AND NOT A MODAL. "Notified" has to reach someone who is already working, and
// the honest reading is *unmissable*, not *blocking*. A dialog across the screen at 09:00 every
// morning is a thing people learn to dismiss without reading, and dismissing it is then the
// habit — which would defeat the one press this whole feature exists to collect. It sits in the
// shell's banner region (beside the no-office alarm) so it is visible on every tab, and it
// leaves when the press lands.
//
// ⚠️ AND NOT A BROWSER PUSH NOTIFICATION. Asking for notification permission is a change to the
// person's browser settings, made on their behalf, for an app they did not open expecting it.
// If the owner wants it, it belongs behind a deliberate "notify me" control — never requested
// silently on mount.
//
// ⚠️ NO DISMISS BUTTON, ON PURPOSE — and it still cannot nag forever. It shows only between the
// first slot's start and the last slot's end, so it stops on its own at the end of the shift
// (marking an arrival after the shift is over is a correction, which is a manager's job). A
// snooze was considered and rejected: the banner says something true — "your shift started and
// no arrival is recorded" — and a control whose only function is to hide a true statement is
// how the feature quietly stops working.
import { scheduledSlotsOnDate } from '../lib/schedule.js'
// Every "should it show / what may it offer" question is answered THERE, from plain values, so
// it can be asked about a 09:00 shift at 08:59 without a browser, a login, or waiting for nine.
import { shiftPromptState } from '../lib/shiftPrompt.js'
import { useOwnAttendance } from '../hooks/useOwnAttendance.js'
import { todayStr, tzTimeStr } from '../lib/clock.js'
import { t } from '../lib/i18n.js'
import { Icon } from './Icon.jsx'

export function ShiftPrompt({ agent, overrideMap = {}, tick = 0 }) {
  // ⚠️ Hooks run before any early return — a conditional hook is a crash, and the shell is the
  // one place where a crash takes the whole app rather than one tab.
  const date = todayStr()
  const att = useOwnAttendance(agent?.id || null)

  if (!agent?.id) return null

  // ⚠️ scheduleHistory is deliberately EMPTY. scheduledSlotsOnDate falls back to the agent's
  // CURRENT schedule only for today and future dates — which is exactly this case — so asking
  // about today needs no history load. It would be wrong for any past date.
  const slots = scheduledSlotsOnDate(agent, date, [], overrideMap)

  // `tick` exists so the clock crossing the shift start re-renders a page nobody touched.
  void tick
  const state = shiftPromptState({
    slots,
    nowHM: tzTimeStr(),
    checkIn: att.checkIn,
    loading: att.loading,
    failed: att.failed,
    loc: att.loc,
  })
  if (!state) return null

  const blocked = state.mode === 'blocked'
  const reasonKey = state.reason ? `my_att_loc_${state.reason}` : null
  const start = state.start

  return (
    <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-3 text-sm text-sky-800 dark:text-sky-300">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-medium">
          <Icon name="clock" size={14} /> {t('shift_prompt_question')}
        </span>
        <span className="text-xs opacity-80 tabular-nums">
          {t('shift_prompt_started_at').replace('{time}', start)}
        </span>

        <span className="grow" />

        {/* THE ACTION MUST BE ONE THEY HAVE. Off the office network, "I came" would be refused
            by the database, so it is not offered at all — the refusal is named and the button
            becomes the thing they CAN do, which is ask again once they have walked in. */}
        {blocked ? (
          <>
            <span className="text-xs text-amber-700 dark:text-amber-400">
              <Icon name="alert-triangle" size={12} /> {reasonKey ? t(reasonKey) : ''}
            </span>
            <button
              className="rounded-lg border border-sky-500/40 px-3 py-1.5 text-xs font-medium hover:bg-sky-500/15 disabled:opacity-40"
              disabled={att.busy} onClick={att.refresh}>
              {t('shift_prompt_recheck')}
            </button>
          </>
        ) : (
          <button
            className="rounded-lg bg-emerald-500/20 border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-40"
            disabled={att.busy} onClick={att.arrive}>
            {t('my_att_arrive')}
          </button>
        )}
      </div>
      {att.err && <p className="mt-1.5 text-xs text-destructive">{att.err}</p>}
    </div>
  )
}
